import React, { useRef } from 'react';
import SignaturePad from 'react-signature-canvas';
import { Box, Button, TextField, Typography, Grid, Paper } from '@mui/material';
import { SignatureData, ReportReceiptSignature } from '../types';
import { base64ToDataURL } from '../utils/riskAssessmentUtils';

interface SignatureSectionProps {
    assessmentSignature: SignatureData;
    reportReceiptSignature: ReportReceiptSignature;
    isViewMode: boolean;
    onAssessmentSignatureChange: (field: keyof SignatureData, value: string) => void;
    onReportReceiptSignatureChange: (field: keyof ReportReceiptSignature, value: string) => void;
}

export const SignatureSection: React.FC<SignatureSectionProps> = ({
    assessmentSignature,
    reportReceiptSignature,
    isViewMode,
    onAssessmentSignatureChange,
    onReportReceiptSignatureChange
}) => {
    const assessmentSignaturePadRef = useRef<any>(null);
    const reportReceiptSignaturePadRef = useRef<any>(null);

    // 獲取當前日期時間的函數
    const getCurrentDateTime = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const handleAssessmentSignature = () => {
        const signaturePad = assessmentSignaturePadRef.current;
        if (signaturePad && signaturePad.getTrimmedCanvas) {
            const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
            onAssessmentSignatureChange('sign', dataUrl);
            // 自動設置當前日期時間
            onAssessmentSignatureChange('dateTime', getCurrentDateTime());
        }
    };

    const handleAssessmentSignatureClear = () => {
        const signaturePad = assessmentSignaturePadRef.current;
        if (signaturePad && signaturePad.clear) {
            signaturePad.clear();
            onAssessmentSignatureChange('sign', '');
            // 清除簽名時也清除日期時間
            onAssessmentSignatureChange('dateTime', '');
        }
    };

    const handleReportReceiptSignature = () => {
        const signaturePad = reportReceiptSignaturePadRef.current;
        if (signaturePad && signaturePad.getTrimmedCanvas) {
            const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
            onReportReceiptSignatureChange('sign', dataUrl);
            // 自動設置當前日期時間
            onReportReceiptSignatureChange('dateTime', getCurrentDateTime());
        }
    };

    const handleReportReceiptSignatureClear = () => {
        const signaturePad = reportReceiptSignaturePadRef.current;
        if (signaturePad && signaturePad.clear) {
            signaturePad.clear();
            onReportReceiptSignatureChange('sign', '');
            // 清除簽名時也清除日期時間
            onReportReceiptSignatureChange('dateTime', '');
        }
    };

    return (
        <>
            {/* 進行上述評估 - 合資格人士簽署 */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1, mb: 2 }}>
                    進行上述評估 - 合資格人士簽署
                </Typography>

                {/* 聲明段落 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2, textAlign: 'justify' }}>
                        本人確認在取得安全主任註冊或合資格人士證明書後，已具備至少一年評估密閉空間內工作工人的安全及健康風險的經驗，並已獲總承建商/分包商/僱主根據《工廠及工業經營(密閉空間)規例》第5(1)條委任進行本危險評估。
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                        此外，本人確認上述評估報告內容是根據《工廠及工業經營(密閉空間)規例》第5(6)條的規定，對密閉空間的工作環境進行評估，並根據該規例就密閉空間內工作工人的安全及健康措施作出建議，而此乃本人所知的所有資料。
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="姓名"
                            value={assessmentSignature.name}
                            onChange={e => onAssessmentSignatureChange('name', e.target.value)}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="日期及時間"
                            type="datetime-local"
                            value={assessmentSignature.dateTime}
                            onChange={e => onAssessmentSignatureChange('dateTime', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>簽名:</Typography>
                        {assessmentSignature.signUrl ? (
                            <img src={assessmentSignature.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                        ) : (
                            !isViewMode && (
                                <>
                                    <SignaturePad
                                        ref={assessmentSignaturePadRef}
                                        canvasProps={{ width: 300, height: 100, style: { border: '1px solid #000' } }}
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <Button size="small" onClick={handleAssessmentSignature}>保存</Button>
                                        <Button size="small" onClick={handleAssessmentSignatureClear}>清除</Button>
                                    </Box>
                                </>
                            )
                        )}
                        {assessmentSignature.sign && (
                            <img
                                src={base64ToDataURL(assessmentSignature.sign)}
                                alt="評估簽名"
                                style={{ width: 300, height: 100, border: '1px solid #ccc', marginTop: 4 }}
                            />
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* 簽收危險評估報告 */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1, mb: 2 }}>
                    簽收 - 危險評估報告人士簽署
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="姓名"
                            value={reportReceiptSignature.name}
                            onChange={e => onReportReceiptSignatureChange('name', e.target.value)}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="職位"
                            value={reportReceiptSignature.position}
                            onChange={e => onReportReceiptSignatureChange('position', e.target.value)}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="日期及時間"
                            type="datetime-local"
                            value={reportReceiptSignature.dateTime}
                            onChange={e => onReportReceiptSignatureChange('dateTime', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>簽名:</Typography>
                        {reportReceiptSignature.signUrl ? (
                            <img src={reportReceiptSignature.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                        ) : (
                            !isViewMode && (
                                <>
                                    <SignaturePad
                                        ref={reportReceiptSignaturePadRef}
                                        canvasProps={{ width: 300, height: 100, style: { border: '1px solid #000' } }}
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <Button size="small" onClick={handleReportReceiptSignature}>保存</Button>
                                        <Button size="small" onClick={handleReportReceiptSignatureClear}>清除</Button>
                                    </Box>
                                </>
                            )
                        )}
                        {reportReceiptSignature.sign && (
                            <img
                                src={base64ToDataURL(reportReceiptSignature.sign)}
                                alt="簽收簽名"
                                style={{ width: 300, height: 100, border: '1px solid #ccc', marginTop: 4 }}
                            />
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
};